import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch user's favorites
  const fetchFavorites = async () => {
    if (!user) {
      setFavorites([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('podcast_names')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = not found
        throw error;
      }

      setFavorites(data?.podcast_names || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setFavorites([]);
    }
  };

  // Add podcast to favorites
  const addToFavorites = async (podcastName: string) => {
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Veuillez vous connecter pour ajouter des favoris",
        variant: "destructive"
      });
      return false;
    }

    setLoading(true);
    try {
      const newFavorites = [...favorites, podcastName];
      
      // First, try to get existing record
      const { data: existingData } = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (existingData) {
        // Update existing record
        const { error } = await supabase
          .from('favorites')
          .update({ podcast_names: newFavorites })
          .eq('user_id', user.id);
        
        if (error) throw error;
      } else {
        // Create new record
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_id: user.id,
            podcast_names: newFavorites
          });
        
        if (error) throw error;
      }

      setFavorites(newFavorites);
      toast({
        title: "❤️ Ajouté aux favoris",
        description: "Le podcast a été ajouté à votre liste",
        duration: 2000,
      });
      return true;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le podcast aux favoris",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Remove podcast from favorites
  const removeFromFavorites = async (podcastName: string) => {
    if (!user) return false;

    setLoading(true);
    try {
      const newFavorites = favorites.filter(name => name !== podcastName);
      
      const { error } = await supabase
        .from('favorites')
        .update({ podcast_names: newFavorites })
        .eq('user_id', user.id);

      if (error) throw error;

      setFavorites(newFavorites);
      toast({
        title: "💔 Retiré des favoris",
        description: "Le podcast a été retiré de votre liste",
        duration: 2000,
      });
      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      toast({
        title: "Erreur",
        description: "Impossible de retirer le podcast des favoris",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Toggle favorite status
  const toggleFavorite = async (podcastName: string) => {
    if (favorites.includes(podcastName)) {
      return await removeFromFavorites(podcastName);
    } else {
      return await addToFavorites(podcastName);
    }
  };

  // Check if podcast is favorite
  const isFavorite = (podcastName: string) => {
    return favorites.includes(podcastName);
  };

  useEffect(() => {
    fetchFavorites();
  }, [user]);

  return {
    favorites,
    loading,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    isFavorite,
    refetch: fetchFavorites
  };
};